import { useState, type ReactNode } from 'react';

interface Props {
  /** Shown as a large ordinal, "01", beside the title. */
  n?: number;
  /** An anchor, for a list of steps above the groups to point at. */
  id?: string;
  title: string;
  hint?: string;
  done?: boolean;
  /** Only a group that is genuinely optional folds. */
  foldable?: boolean;
  startFolded?: boolean;
  /** One line describing the current answer, shown while folded. */
  summary?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
}

/**
 * One group of decisions.
 *
 * The call sheet this replaces had eight numbered steps, every one foldable
 * and every one looking equally load-bearing — so the two that a shoot cannot
 * happen without sat in a queue of six that have working defaults. Here the
 * required groups are simply open, and only what is genuinely optional folds
 * away behind a line saying what it currently is.
 */
export function Group({ n, id, title, hint, done, foldable, startFolded, summary, aside, children }: Props) {
  const [open, setOpen] = useState(!startFolded);
  const bodyId = `group-${title.replace(/\s+/g, '-').toLowerCase()}`;
  const shut = foldable && !open;

  return (
    <section id={id} className={`group ${done ? 'is-done' : ''} ${shut ? 'is-shut' : ''}`}>
      <div className="group__head">
        {n !== undefined && <span className="group__n">{String(n).padStart(2, '0')}</span>}
        <h2 className="group__title">{title}</h2>
        {hint && <span className="group__hint">{hint}</span>}
        <div className="grow" />
        {shut && summary && <span className="group__summary">{summary}</span>}
        {!shut && aside}
        {foldable && (
          <button
            type="button"
            className="btn btn--link btn--sm group__fold"
            aria-expanded={open}
            aria-controls={bodyId}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? '−' : '+'}
          </button>
        )}
      </div>
      {!shut && (
        <div id={bodyId} className="group__body">
          {children}
        </div>
      )}
    </section>
  );
}
